import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, profile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const channelsRef = useRef([]);
  const consultationTimersRef = useRef([]);

  // Keep stable refs to callbacks so the subscription useEffect
  // does NOT need them as dependencies (prevents subscription churn)
  const createNotificationRef = useRef(null);
  const scheduleReminderRef = useRef(null);
  const sendEmailRef = useRef(null);
  const userRef = useRef(null);
  const profileRef = useRef(null);

  // Sync refs on every render
  userRef.current = user;
  profileRef.current = profile;

  // Fetch existing notifications
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(50);

      if (error) {
        console.error('Error fetching notifications:', error?.message);
        return;
      }

      setNotifications(data || []);
      setUnreadCount((data || [])?.filter(n => !n?.is_read)?.length);
    } catch (err) {
      console.error('fetchNotifications error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Insert a notification into DB
  const createNotification = useCallback(async ({
    userId,
    notificationType,
    title,
    message,
    relatedId = null,
    link = null,
    category = 'system',
    metadata = {}
  }) => {
    try {
      const { data, error } = await supabase?.from('notifications')?.insert({
          user_id: userId,
          notification_type: notificationType,
          title,
          message,
          related_id: relatedId,
          link,
          category,
          metadata,
          is_read: false
        })?.select()?.single();

      if (error) {
        console.error('Error creating notification:', error?.message);
      }
      return data;
    } catch (err) {
      console.error('createNotification error:', err?.message);
    }
  }, []);

  // Mark a single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId)?.eq('user_id', user?.id);

      if (error) {
        console.error('Error marking notification as read:', error?.message);
        return;
      }

      setNotifications(prev =>
        prev?.map(n => n?.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('markAsRead error:', err?.message);
    }
  }, [user?.id]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', user?.id)?.eq('is_read', false);

      if (error) {
        console.error('Error marking all as read:', error?.message);
        return;
      }

      setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('markAllAsRead error:', err?.message);
    }
  }, [user?.id]);

  // Send email notification via edge function
  const sendEmailNotification = useCallback(async ({ to, subject, type, data }) => {
    try {
      await supabase?.functions?.invoke('send-notification-email', {
        body: { to, subject, type, data }
      });
    } catch (err) {
      console.error('sendEmailNotification error:', err?.message);
    }
  }, []);

  // Schedule consultation reminders (15 min before)
  const scheduleConsultationReminder = useCallback((consultation) => {
    if (!userRef?.current?.id) return;
    const consultationDateTime = new Date(
      `${consultation.consultation_date}T${consultation.consultation_time}`
    );
    const reminderTime = new Date(consultationDateTime.getTime() - 15 * 60 * 1000);
    const now = new Date();
    const delay = reminderTime?.getTime() - now?.getTime();

    if (delay > 0 && delay < 24 * 60 * 60 * 1000) {
      const timer = setTimeout(async () => {
        await createNotificationRef?.current?.({
          userId: userRef?.current?.id,
          notificationType: 'appointment',
          title: 'Consultation Reminder',
          message: `Your consultation "${consultation?.title}" starts in 15 minutes.`,
          relatedId: consultation?.id,
          link: '/video-consultation',
          category: 'consultation',
          metadata: { consultationId: consultation?.id }
        });
      }, delay);
      consultationTimersRef?.current?.push(timer);
    }
  }, []);

  // Keep callback refs up-to-date without triggering subscription re-runs
  useEffect(() => {
    createNotificationRef.current = createNotification;
  }, [createNotification]);

  useEffect(() => {
    scheduleReminderRef.current = scheduleConsultationReminder;
  }, [scheduleConsultationReminder]);

  useEffect(() => {
    sendEmailRef.current = sendEmailNotification;
  }, [sendEmailNotification]);

  // Helper: tear down all active channels and timers
  const cleanupSubscriptions = useCallback(() => {
    channelsRef?.current?.forEach(ch => {
      try { supabase?.removeChannel(ch); } catch (_) {}
    });
    channelsRef.current = [];
    consultationTimersRef?.current?.forEach(t => clearTimeout(t));
    consultationTimersRef.current = [];
  }, []);

  // Setup real-time subscriptions — only depends on user.id and profile.role
  // All other logic accessed via stable refs to prevent duplicate subscriptions
  useEffect(() => {
    if (!user?.id || !profile?.role) {
      cleanupSubscriptions();
      return;
    }

    // Prevent duplicate channels if already subscribed for this user
    if (channelsRef?.current?.length > 0) {
      cleanupSubscriptions();
    }

    const userId = user?.id;
    const userRole = profile?.role;
    const channels = [];

    // ── 1. Notifications table subscription (own notifications) ──
    const notifChannel = supabase
      ?.channel(`notifications:${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const newNotif = payload?.new;
          setNotifications(prev => [newNotif, ...prev]?.slice(0, 50));
          setUnreadCount(prev => prev + 1);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const updated = payload?.new;
          setNotifications(prev =>
            prev?.map(n => n?.id === updated?.id ? updated : n)
          );
          setUnreadCount(prev => {
            const wasUnread = !payload?.old?.is_read;
            const isNowRead = updated?.is_read;
            if (wasUnread && isNowRead) return Math.max(0, prev - 1);
            return prev;
          });
        }
      )
      ?.on('system', {}, (status) => {
        // Reconnect on channel error or close
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.warn('Notification channel lost, reconnecting...');
          setTimeout(() => {
            if (userRef?.current?.id && profileRef?.current?.role) {
              cleanupSubscriptions();
              // Re-trigger by forcing a state update is not needed;
              // the channel will be re-subscribed on next effect run.
              // Instead we directly re-subscribe the channel.
              notifChannel?.subscribe();
            }
          }, 3000);
        }
      })
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Channel is live
        }
      });
    channels?.push(notifChannel);

    // ── 2. Cases table subscription ──
    const casesChannel = supabase
      ?.channel(`cases:${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pro_bono_cases'
        },
        async (payload) => {
          const updatedCase = payload?.new;
          const oldCase = payload?.old;

          // Notify client when case is assigned
          if (!oldCase?.assigned_lawyer_id && updatedCase?.assigned_lawyer_id) {
            if (updatedCase?.client_id === userId) {
              await createNotificationRef?.current?.({
                userId,
                notificationType: 'system',
                title: 'Lawyer Assigned to Your Case',
                message: `A lawyer has been assigned to your case: "${updatedCase?.case_title}".`,
                relatedId: updatedCase?.id,
                link: '/client-portal',
                category: 'case',
                metadata: { caseId: updatedCase?.id, caseTitle: updatedCase?.case_title }
              });
            }
          }

          // Notify when case status changes
          if (oldCase?.assignment_status !== updatedCase?.assignment_status) {
            const isRelevant =
              updatedCase?.client_id === userId ||
              updatedCase?.assigned_lawyer_id === userId;

            if (isRelevant) {
              const statusLabels = {
                assigned: 'Assigned',
                in_progress: 'In Progress',
                completed: 'Completed',
                closed: 'Closed'
              };
              const newStatus = statusLabels?.[updatedCase?.assignment_status] || updatedCase?.assignment_status;
              await createNotificationRef?.current?.({
                userId,
                notificationType: 'system',
                title: 'Case Status Updated',
                message: `Case "${updatedCase?.case_title}" status changed to ${newStatus}.`,
                relatedId: updatedCase?.id,
                link: userRole === 'client' ? '/client-portal' : '/case-management',
                category: 'case',
                metadata: { caseId: updatedCase?.id, newStatus }
              });
            }
          }
        }
      )
      ?.subscribe();
    channels?.push(casesChannel);

    // ── 3. Consultations table subscription ──
    const consultationsChannel = supabase
      ?.channel(`consultations:${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'consultations'
        },
        async (payload) => {
          const consultation = payload?.new;
          const isRelevant =
            consultation?.client_id === userId ||
            consultation?.lawyer_id === userId;

          if (isRelevant) {
            await createNotificationRef?.current?.({
              userId,
              notificationType: 'appointment',
              title: 'New Consultation Scheduled',
              message: `Consultation "${consultation?.title}" has been scheduled for ${consultation?.consultation_date}.`,
              relatedId: consultation?.id,
              link: '/video-consultation',
              category: 'consultation',
              metadata: { consultationId: consultation?.id }
            });
            scheduleReminderRef?.current?.(consultation);
          }
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'consultations'
        },
        async (payload) => {
          const consultation = payload?.new;
          const oldConsultation = payload?.old;
          const isRelevant =
            consultation?.client_id === userId ||
            consultation?.lawyer_id === userId;

          if (isRelevant && oldConsultation?.status !== consultation?.status) {
            const statusMessages = {
              confirmed: 'Your consultation has been confirmed.',
              cancelled: 'Your consultation has been cancelled.',
              completed: 'Your consultation has been marked as completed.'
            };
            const msg = statusMessages?.[consultation?.status] ||
              `Consultation status changed to ${consultation?.status}.`;

            await createNotificationRef?.current?.({
              userId,
              notificationType: 'appointment',
              title: `Consultation ${consultation?.status === 'confirmed' ? 'Confirmed' : 'Updated'}`,
              message: `"${consultation?.title}": ${msg}`,
              relatedId: consultation?.id,
              link: '/video-consultation',
              category: 'consultation',
              metadata: { consultationId: consultation?.id, newStatus: consultation?.status }
            });

            // Send email for confirmed consultations
            if (consultation?.status === 'confirmed') {
              const targetEmail = consultation?.client_id === userId
                ? profileRef?.current?.email
                : null;
              if (targetEmail) {
                await sendEmailRef?.current?.({
                  to: targetEmail,
                  subject: 'Consultation Confirmed - LawDesk KE',
                  type: 'consultation_confirmed',
                  data: { consultationTitle: consultation?.title, date: consultation?.consultation_date }
                });
              }
            }
          }
        }
      )
      ?.subscribe();
    channels?.push(consultationsChannel);

    // ── 4. Documents table subscription ──
    const documentsChannel = supabase
      ?.channel(`documents:${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'documents'
        },
        async (payload) => {
          const doc = payload?.new;

          // Notify lawyers when a client uploads a document
          if (userRole === 'lawyer' || userRole === 'partner' || userRole === 'admin') {
            if (doc?.uploaded_by !== userId) {
              await createNotificationRef?.current?.({
                userId,
                notificationType: 'document',
                title: 'New Document Uploaded',
                message: `A client has uploaded a new document: "${doc?.name}".`,
                relatedId: doc?.id,
                link: '/document-management',
                category: 'document',
                metadata: { documentId: doc?.id, documentName: doc?.name }
              });
            }
          }
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'documents'
        },
        async (payload) => {
          const doc = payload?.new;
          const oldDoc = payload?.old;

          // Notify client when lawyer reviews/changes document status
          if (oldDoc?.status !== doc?.status && doc?.client_id === userId) {
            const statusMessages = {
              approved: 'has been approved',
              verified: 'has been verified',
              rejected: 'has been rejected',
              signed: 'has been signed'
            };
            const statusMsg = statusMessages?.[doc?.status] || `status changed to ${doc?.status}`;

            await createNotificationRef?.current?.({
              userId,
              notificationType: 'document',
              title: 'Document Status Updated',
              message: `Your document "${doc?.name}" ${statusMsg}.`,
              relatedId: doc?.id,
              link: '/client-portal',
              category: 'document',
              metadata: { documentId: doc?.id, newStatus: doc?.status }
            });

            // Send email for document reviewed
            if (['approved', 'verified', 'rejected']?.includes(doc?.status)) {
              await sendEmailRef?.current?.({
                to: profileRef?.current?.email,
                subject: `Document ${doc?.status === 'rejected' ? 'Rejected' : 'Reviewed'} - LawDesk KE`,
                type: 'document_reviewed',
                data: { documentName: doc?.name, status: doc?.status }
              });
            }
          }
        }
      )
      ?.subscribe();
    channels?.push(documentsChannel);

    channelsRef.current = channels;

    // Fetch upcoming consultations to schedule reminders
    // Clear existing timers first to prevent duplicates on re-subscribe
    consultationTimersRef?.current?.forEach(t => clearTimeout(t));
    consultationTimersRef.current = [];

    const fetchUpcomingConsultations = async () => {
      try {
        const today = new Date()?.toISOString()?.split('T')?.[0];
        const { data } = await supabase?.from('consultations')?.select('*')?.or(`client_id.eq.${userId},lawyer_id.eq.${userId}`)?.gte('consultation_date', today)?.eq('status', 'confirmed');

        (data || [])?.forEach(c => scheduleReminderRef?.current?.(c));
      } catch (err) {
        console.error('fetchUpcomingConsultations error:', err?.message);
      }
    };
    fetchUpcomingConsultations();

    return () => {
      cleanupSubscriptions();
    };
  // Only re-subscribe when the authenticated user or their role changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profile?.role]);

  // Fetch notifications when user logs in
  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user?.id, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    createNotification,
    fetchNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
